import { useMemo } from "react"
import { router } from "@inertiajs/react"
import ProductCard from "@/components/ttt/ProductCard"
import type {
  TttCardItem,
  TttCardI18n,
  TttLibraryContent,
} from "@/components/ttt/types"
import type {
  Category,
  SortOption,
  FilterState,
  CatalogLabels,
} from "@/components/storefront/types"

interface TttStoreCatalogProps {
  labels: CatalogLabels
  urls: { root: string; store: string }
  categories: Category[]
  items: TttCardItem[]
  filters: FilterState
  sortOptions: SortOption[]
  i18n: TttCardI18n
  holiday: boolean
  holidaySentence: string | null
  backpackSrc: string
  library: boolean
  libraryUrl: string | null
  libraryContent: TttLibraryContent
}

// Faithful React port of app/views/stores/show.html.slim + _filters.html.slim
// for TTT: a persistent left sidebar (search, sort, category checkboxes — no
// price filter) and the TTT hachures card grid on the right.
export default function StoreCatalog({
  labels,
  urls,
  categories,
  items,
  filters,
  sortOptions,
  i18n,
  holiday,
  holidaySentence,
  backpackSrc,
  library,
  libraryUrl,
  libraryContent,
}: TttStoreCatalogProps) {
  const selectedCategories = useMemo(
    () => new Set(filters.selected_categories),
    [filters.selected_categories]
  )

  const visitWithFilters = (overrides: Partial<{
    query: string
    sort: string
    categories: string[]
  }>) => {
    const query = overrides.query ?? filters.query
    const sort = overrides.sort ?? filters.sort
    const selected = overrides.categories ?? filters.selected_categories

    const filtersParam: Record<string, string | number> = { query, sort }
    categories.forEach((category) => {
      filtersParam[category.name] = selected.includes(category.name) ? 1 : 0
    })

    router.get(
      urls.store,
      { filters: filtersParam },
      { preserveScroll: true, preserveState: true }
    )
  }

  const toggleCategory = (categoryName: string) => {
    const next = new Set(selectedCategories)
    if (next.has(categoryName)) {
      next.delete(categoryName)
    } else {
      next.add(categoryName)
    }
    visitWithFilters({ categories: Array.from(next) })
  }

  return (
    <div className="bg-light md:pt-24 min-h-[calc(100vh-10rem)]">
      {/* Library mode tints bg-light / text-primary green, page-wide (like the
          legacy <style> block in stores/show.html.slim). */}
      {library && (
        <style>
          {".bg-light{background-color:#F7FFF5 !important}.text-primary{color:#71D1AF !important}"}
        </style>
      )}

      <main className="mx-auto max-w-2xl px-4 lg:max-w-7xl lg:px-8">
        {library && (
          <div className="pt-6">
            <div
              className="bg-center bg-cover bg-no-repeat h-12 w-1/4 flex items-center justify-end text-light text-sm font-bold mb-6"
              style={{ backgroundImage: `url(${libraryContent.green_dot})` }}
            >
              <span className="pr-3">{libraryContent.space}</span>
            </div>
            <div className="text-primary bg-white bg-opacity-80 rounded-xl shadow-md p-6 mb-8">
              <h2 className="text-xl font-bold mb-2">{libraryContent.description}</h2>
              <p className="mb-4 text-content">{libraryContent.info}</p>

              <div className="mb-4">
                <h3 className="font-semibold mb-1">{libraryContent.pricing_title}</h3>
                <ul className="list-disc list-inside text-content">
                  {libraryContent.pricing_tiers.map((tier) => (
                    <li key={tier}>{tier}</li>
                  ))}
                </ul>
              </div>

              <div className="mb-4">
                <h3 className="font-semibold mb-1">{libraryContent.billing_title}</h3>
                <p className="text-content">{libraryContent.billing_description}</p>
              </div>

              <div className="mb-4">
                <h3 className="font-semibold mb-1">{libraryContent.contact_title}</h3>
                <p className="text-content">
                  {libraryContent.contact_description}
                  <a
                    href={`mailto:${libraryContent.contact_email}`}
                    className="text-contrast font-semibold ml-1"
                  >
                    {libraryContent.contact_email}
                  </a>
                </p>
              </div>

              <div className="mb-2">
                <h3 className="font-semibold mb-1">{libraryContent.payment_title}</h3>
                <p className="text-content">{libraryContent.payment_description}</p>
              </div>
            </div>
          </div>
        )}

        <div className="pb-24 pt-12 lg:grid lg:grid-cols-3 lg:gap-x-8 xl:grid-cols-4 min-h-[calc(100vh-10rem)]">
          <aside>
            <h2 className="sr-only">{labels.filter}</h2>
            <div className="hidden lg:block">
              <div className="space-y-10 divide-y divide-content">
                <div>
                  <span className="block text-sm font-medium text-contrast">
                    {labels.filter}
                  </span>
                  <div className="space-y-4 pt-6">
                    <div>
                      <label className="block text-sm text-midgray mb-1">
                        Recherche
                      </label>
                      <input
                        type="text"
                        value={filters.query}
                        onChange={(e) =>
                          visitWithFilters({ query: e.target.value })
                        }
                        className="w-full rounded border-content text-primary focus:ring-primary focus:border-primary"
                      />
                    </div>

                    <select
                      value={filters.sort}
                      onChange={(e) => visitWithFilters({ sort: e.target.value })}
                      className="w-full rounded border-content text-primary focus:ring-primary focus:border-primary"
                    >
                      {sortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                    <div className="space-y-3 pt-2">
                      {categories.map((category, index) => {
                        const active = selectedCategories.has(category.name)
                        return (
                          <label
                            key={category.id}
                            htmlFor={`category-${index}`}
                            className="flex items-center gap-3 cursor-pointer"
                          >
                            <input
                              id={`category-${index}`}
                              type="checkbox"
                              checked={active}
                              onChange={() => toggleCategory(category.name)}
                              className="h-6 w-4 checked:!bg-primary rounded border-content text-primary focus:ring-primary"
                            />
                            <span className="text-sm text-midgray">
                              {category.label ?? category.name}
                            </span>
                          </label>
                        )
                      })}
                    </div>

                    {libraryUrl && (
                      <a
                        href={libraryUrl}
                        className="bg-center bg-cover bg-no-repeat h-12 w-full flex items-center justify-end text-light text-sm font-bold transition-all duration-300 cursor-pointer hover:-translate-x-10"
                        style={{ backgroundImage: `url(${libraryContent.green_dot})` }}
                      >
                        <span className="pr-3">{libraryContent.access}</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <section
            className="mt-6 lg:col-span-2 lg:mt-0 xl:col-span-3"
            aria-labelledby="product-heading"
          >
            <h2 id="product-heading" className="sr-only">
              {labels.title}
            </h2>
            {holiday && holidaySentence ? (
              <div className="flex items-center justify-center py-24">
                <h1 className="text-4xl text-midgray">{holidaySentence}</h1>
              </div>
            ) : items.length === 0 ? (
              <div className="flex items-center justify-center py-24">
                <h1 className="text-4xl text-midgray">{labels.no_products}</h1>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-x-8 gap-y-16 h-full sm:grid-cols-2 sm:gap-x-12 sm:gap-y-16 lg:gap-x-16 xl:grid-cols-3">
                {items.map((item) => (
                  <ProductCard
                    key={item.id}
                    item={item}
                    backpackSrc={backpackSrc}
                    holiday={holiday}
                    i18n={i18n}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  )
}
